import { Component, inject, OnInit, signal } from "@angular/core";
import { SharedModule } from "src/shared/shared.module";
import { CourseItemComponent } from "./component/course-item/course-item.component";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { CourseService } from "src/app/services/api/courses.service";
import { debounceTime, distinctUntilChanged, forkJoin, lastValueFrom, merge, Observable } from "rxjs";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { AuthService } from "../auth";
import { LabelMode } from "src/utils/label-mode.enum";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { subjectArt } from "src/app/config/subject-art";
import { ToastService } from "src/app/services/toast-service/toast.service";

@Component({
  selector: "app-courses",
  imports: [
    SharedModule,
    CourseItemComponent,
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: "./courses.component.html",
  styleUrl: "./courses.component.scss",
})
export class CoursesComponent implements OnInit {
  /** The category's illustration, or null — same map the landing strip uses. */
  art(name?: string): string | null {
    return subjectArt(name);
  }

  courseItems = [1, 2, 3, 4];

  isLoading = signal(true);
  tableLoading = signal(true);

  courseCategories: any = [];
  /** `null` clears the filter; the select's own clear button does the same. */
  trialOptions = [{ id: 1, name: "เฉพาะคอร์สที่ทดลองเรียนได้", value: true }];

  sortOptions = [
    {
      id: 1,
      name: "คอร์สขายดี",
      value: "best_seller desc",
    },
    {
      id: 2,
      name: "รีวิวสูงสุด",
      value: "total_review desc",
    },
    {
      id: 3,
      name: "อัปเดตล่าสุด",
      value: "updated_at desc",
    },
  ];
  dataSource: any;
  columns: any;
  form: FormGroup;

  #courseService = inject(CourseService);
  #authService = inject(AuthService);
  #router = inject(Router);
  #route = inject(ActivatedRoute);
  #fb = inject(FormBuilder);
  #toastService = inject(ToastService);
  #translate = inject(TranslateService);

  LabelMode = LabelMode;

  ngOnInit(): void {
    this.initColumns();
    this.buildForm();

    // Deep link: /courses?trial=1. Only read when the key is actually present,
    // so changing category (which re-emits the query params) cannot silently
    // clear a filter the visitor set.
    const trial = this.#route.snapshot.queryParamMap.get("trial");
    if (trial !== null) {
      this.form.patchValue({ has_trial: trial === "1" || trial === "true" ? true : null });
    }

    this.getMasterData();
  }

  buildForm() {
    this.form = this.#fb.group({
      datatable: true,
      page: 1,
      order: "best_seller desc",
      limit: 4,
      keywords: null,
      education_id: null,
      semester_id: null,
      course_category_id: null,
      course_category_ids: null,
      // Narrows the list to courses that expose a preview video. Read from the
      // URL below so /courses?trial=1 works as a campaign link.
      has_trial: null,
    });

    merge(
      this.form.get("course_category_id")?.valueChanges as Observable<any>,
      this.form.get("order")?.valueChanges as Observable<any>,
      this.form.get("page")?.valueChanges as Observable<any>,
      this.form.get("has_trial")?.valueChanges as Observable<any>,
    )
      .pipe(debounceTime(100))
      .subscribe((value) => {
        this.fetchCourseList();
      });

    // Typing is its own stream: the selects fire once per choice, but a search
    // box fires per keystroke and would otherwise put a request on the wire for
    // every letter. The catalogue was the only browse page in the app without
    // one — /blogs has had a working search all along, and the 2021 build had
    // keyword search here too.
    this.form
      .get("keywords")
      ?.valueChanges.pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.form.patchValue({ page: 1 }, { emitEvent: false });
        this.fetchCourseList();
      });
  }

  initColumns() {
    this.columns = [
      {
        name: "TABLE.BILLS",
        prop: "name",
        sub_prop: "bill_date",
        headerClass: "w-350px min-w-150px",
        propClass: "text-hover-primary",
        sortable: false,
      },
      {
        name: "TABLE.TOTAL_PAYMENT",
        prop: "course_category_name",
        sub_prop: "status",
        headerClass: "w-300px min-w-100px",
        subPropClass: ["text-warning"],
        statuses: [4],
        sortable: false,
        currency: true,
      },
      {
        name: "TABLE.SELLER",
        prop: "explain",
        sub_prop: "seller_role",
        headerClass: "w-300px min-w-150px",
        sortable: false,
      },
    ];
  }

  _setLoading(value: boolean, time: number = 500) {
    setTimeout(() => {
      this.isLoading.update((_) => value);
    }, time);
  }

  getMasterData() {
    const params = {
      is_recommend: true,
      is_disabled: false,
    };

    this._setLoading(true, 0);
    forkJoin([
      this.#courseService.getCourseCategories() as Observable<any>,
      this.#courseService.getCourses(params) as Observable<any>,
      this.#courseService.getCourses(
        this.form.getRawValue(),
      ) as Observable<any>,
    ]).subscribe({
      next: ([categories, courses, datatable]) => {
        this.courseCategories = categories;
        this.courseCategories.map((cate: any) => {
          cate.recommend_courses = courses.filter(
            (course: any) => course.course_category_id === cate.id,
          );
        });
        this.dataSource = datatable;
      },
      // `error` on a subscribe object does not also fire `complete` — without
      // this the loading flags this sets true at the top of the method never
      // came back down, so a failed request left the whole page on its
      // skeleton forever with nothing on screen to say why.
      error: (error) => {
        this._setLoading(false);
        this.tableLoading.update((_) => false);
        this.#toastService.toastError(this.#translate.instant("APP.LOAD_ERROR"));
      },
      complete: () => {
        this._setLoading(false);
        this.tableLoading.update((_) => false);
      },
    });
  }

  async fetchCourseList() {
    this.tableLoading.update((_) => true);
    let payload = this.form.getRawValue();
    try {
      const courseFiltered = await lastValueFrom(
        this.#courseService.getCourses(payload),
      );
      this.dataSource = courseFiltered;
      setTimeout(() => {
        this.tableLoading.update((_) => false);
      }, 500);
    } catch (error) {
      // Same trap as above: the empty catch left `tableLoading` stuck true —
      // a search or filter that failed showed the skeleton forever.
      this.tableLoading.update((_) => false);
      this.#toastService.toastError(this.#translate.instant("APP.LOAD_ERROR"));
    }
  }

  onRowClick(row: any) {
    this.#router.navigate([`courses/${row.id}`]);
  }
  onTableChange(event: Event) {
    this.form.patchValue(event);
  }
}
